import { memo, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  FormControlLabel,
  Switch,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import toast from "react-hot-toast";
import axios from "axios";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { weightSchema } from "../../app/schema";
import { useDispatch } from "react-redux";
import { showModal, hideConfirmModal } from "../../redux/counterSlice";
import { FormInputText } from "../../components/form-components/FormInputText";
import { FormInputDropdown } from "../form-components/FormInputDropdown";

const AddWeight = ({
  open,
  handleClose,
  selectedRow: selectedWeightRow,
  loadInitialData,
}) => {
  const dispatch = useDispatch();
  const isEditMode = selectedWeightRow && selectedWeightRow.weight_id;

  const [unitList, setUnitList] = useState([]);

  const methods = useForm({
    resolver: yupResolver(weightSchema),
    defaultValues: {
      weight_name: "",
      allow_cod: false,
      unit_id: "",
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      const res = await axios.get("/getAllActiveUnitList");
      if (Array.isArray(res.data)) {
        setUnitList(res.data);
      } else if (Array.isArray(res.data.data)) {
        setUnitList(res.data.data);
      } else {
        toast.error("Unexpected response from unit API");
      }
    } catch (error) {
      toast.error("Failed to fetch units");
    }
  };

  useEffect(() => {
    if (isEditMode && selectedWeightRow) {
      reset({
        weight_name: selectedWeightRow?.weight_name || "",
        allow_cod: !!selectedWeightRow?.allow_cod,
        unit_id: selectedWeightRow?.unit_id || "",
      });
    } else {
      reset({
        weight_name: "",
        allow_cod: false,
        unit_id: "",
      });
    }
  }, [selectedWeightRow, isEditMode, reset]);

  const onSubmit = async (data) => {
    const payload = {
      weight_name: data.weight_name,
      allow_cod: data.allow_cod ? 1 : 0,
      unit_id: data.unit_id,
      ...(isEditMode && { weight_id: selectedWeightRow.weight_id }),
    };

    try {
      const endpoint = isEditMode ? "/updateWeight" : "/createWeight";
      const response = await axios.post(endpoint, payload);

      if (!response?.data?.error) {
        toast.success(response?.data?.message || "Saved successfully");
        loadInitialData();
        reset();
        handleClose();
      } else {
        let errDesc = response?.data?.message || "Something went wrong";
        if (response?.data?.data) {
          errDesc = Object.values(response.data.data).join("\n");
        }
        dispatch(showModal({ msg: errDesc, error: true }));
      }
    } catch (error) {
      dispatch(
        showModal({ msg: error?.message || "Server error", error: true })
      );
    } finally {
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className="text-primary">
        {isEditMode ? "Edit Weight" : "Add Weight"}
      </DialogTitle>
      <DialogContent>
        <form id="add-weight-form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormInputText
                name="weight_name"
                control={control}
                label="Weight Name"
                errors={errors}
                mandatory="true"
              />
            </Grid>

            <Grid item xs={12}>
              <FormInputDropdown
                name="unit_id"
                control={control}
                label="Unit Name"
                type="single"
                options={unitList.map((unit) => ({
                  id: unit.unit_id || unit.id,
                  name: unit.unit_name,
                }))}
                errors={errors}
                defaultValue={selectedWeightRow?.unit_id}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Controller
                    name="allow_cod"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        checked={field.value}
                        color="primary"
                      />
                    )}
                  />
                }
                label="Allow COD"
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="error">
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-weight-form"
          color="primary"
          variant="contained"
          startIcon={<SaveIcon />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddWeight);
